import { useMemo } from 'react';

import { useProductMenuCode } from '~/components/product/ProductMenuCodeContext';

import { useUserInfoStore } from '~/store';
import {
  transformToProductDeliveryTypeOptions as transformToDeliveryTypeSelectOptions,
  transformToProductSelectOptions as transformToSelectOptions,
} from '~/utils';

import { PRODUCT_USAGE_STATUS } from '~/constants/product';

import { useGetCodesMenuMenuCodeQuery } from './useGetCodesMenuMenuCodeQuery';

import type { TypeProductModalOptions } from '~/types/product.types';

/**
 * [상품관리] 상품 모달 셀렉트 옵션 조회 훅
 * @description Context의 menuCode로 codesMenu를 조회하고, 모달에서 사용하는 셀렉트 옵션으로 변환
 */
export const useProductModalOptions = (): TypeProductModalOptions | null => {
  const menuCode = useProductMenuCode();
  const hasPermission = useUserInfoStore((state) => state.hasPermission);
  const hasPermissionFBK = hasPermission([{ ownerType: 'FBK' }]);

  const codesMenuQuery = useGetCodesMenuMenuCodeQuery<
    Record<string, { codeItemList: { code: string; name: string }[] }>
  >({
    menuCode,
  });

  return useMemo(() => {
    if (!codesMenuQuery.data) return null;

    const data = codesMenuQuery.data;

    return {
      unit: transformToSelectOptions(data.unit.codeItemList),
      keepLevel: transformToSelectOptions(data.keepLevel.codeItemList),
      combinedPacking: transformToSelectOptions(data.combinedPacking.codeItemList),
      oneDayProduct: transformToSelectOptions(data.oneDayProduct.codeItemList),
      shelfLifeType: transformToSelectOptions(data.shelfLifeType.codeItemList),
      fragile: transformToSelectOptions(data.fragile.codeItemList),
      wettable: transformToSelectOptions(data.wettable.codeItemList),
      needRefrigerant: transformToSelectOptions(data.needRefrigerant.codeItemList),
      packingDirection: transformToSelectOptions(data.packingDirection.codeItemList),
      cosmeticPacking: transformToSelectOptions(data.cosmeticPacking.codeItemList),
      inspection: transformToSelectOptions(data.inspection.codeItemList),
      kurlyKeepType: transformToSelectOptions(data.kurlyKeepType.codeItemList),
      deliveryDispatchType: transformToSelectOptions(data.deliveryDispatchType.codeItemList),
      deliveryType: transformToDeliveryTypeSelectOptions(data.deliveryType.codeItemList),
      deliveryDocumentNum: transformToDeliveryTypeSelectOptions(data.deliveryDocumentNum.codeItemList),
      massDocumentNum: transformToDeliveryTypeSelectOptions(data.massDocumentNum.codeItemList),
      productOperationType: transformToSelectOptions(data.productOperationType.codeItemList),
      productUsageStatus: hasPermissionFBK
        ? transformToSelectOptions(data.productUsageStatus.codeItemList)
        : Object.values(PRODUCT_USAGE_STATUS),
      sellingType: transformToSelectOptions(data.sellingType.codeItemList),
    };
  }, [codesMenuQuery.data, hasPermissionFBK]);
};
